import { ExternalLink } from 'lucide-react';
import {
  getAddressBlockExplorerUrl,
  getNetworkDisplayName,
  type NetworkId,
  type NetworkType,
} from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import { cn, formatAddress } from '@/lib/utils';
import { CopyAddressButton } from './copy-address-button';

type AddressDisplayProps = {
  address: string;
  name?: string | null | undefined;
  networkId?: NetworkType | NetworkId | null | undefined;
  showAddressName?: boolean;
  addressNameLayout?: 'stacked' | 'inline';
  addressNameClassName?: string;
  showNetwork?: boolean;
  truncateAddress?: boolean;
  centerWhenNoLabel?: boolean | undefined;
  showCopy?: boolean;
  showBlockExplorerLink?: boolean;
  className?: string;
};

/**
 * Canonical address display for label-first UI.
 *
 * Behavior:
 * - if `showAddressName` and `name` exist, render the label as primary text
 *   and the address as supporting text
 * - otherwise render the address as the only visible text
 * - copy/explorer actions always target the underlying address
 */
export function AddressDisplay({
  address,
  name = null,
  networkId = null,
  showAddressName = true,
  addressNameLayout = 'stacked',
  addressNameClassName,
  showNetwork = false,
  truncateAddress = true,
  centerWhenNoLabel = false,
  showCopy = false,
  showBlockExplorerLink = false,
  className,
}: AddressDisplayProps) {
  const trimmedName = name?.trim() || null;
  const showLabel = showAddressName && Boolean(trimmedName);
  const renderedAddress = truncateAddress ? formatAddress(address) : address;
  const renderedNetwork =
    showNetwork && networkId
      ? getNetworkDisplayName(networkId as NetworkType)
      : null;
  const explorerUrl =
    showBlockExplorerLink && networkId
      ? getAddressBlockExplorerUrl(address, networkId as NetworkType)
      : null;
  // These actions always target the underlying address, not the label.
  const actions =
    showCopy || explorerUrl ? (
      <div className="flex shrink-0 items-center gap-0">
        {showCopy ? <CopyAddressButton address={address} /> : null}

        {explorerUrl ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            asChild
            className="-ml-0.5 h-5 w-5 rounded-md p-0 text-muted-foreground hover:text-foreground"
          >
            <a
              href={explorerUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="View address on block explorer"
              title="View on block explorer"
            >
              <ExternalLink className="size-3.5" />
            </a>
          </Button>
        ) : null}
      </div>
    ) : null;

  // Case: compact one-line layout for tight read-only surfaces.
  if (showLabel && addressNameLayout === 'inline') {
    return (
      <div
        className={cn(
          'inline-flex min-w-0 max-w-full items-center gap-1',
          className
        )}
      >
        <span
          className={cn(
            'min-w-0 shrink truncate text-xs font-medium text-foreground',
            addressNameClassName
          )}
        >
          {trimmedName}
        </span>
        <div className="flex min-w-0 shrink items-center gap-1">
          {renderedNetwork ? (
            <>
              <span className="shrink-0 text-xs text-muted-foreground">
                {renderedNetwork}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">·</span>
            </>
          ) : null}
          <span className="min-w-0 truncate font-mono text-xs text-muted-foreground">
            {renderedAddress}
          </span>
        </div>
        {actions}
      </div>
    );
  }

  // Case: stacked layout keeps the label visually primary.
  return (
    <div
      className={cn(
        'min-w-0 max-w-full overflow-hidden',
        centerWhenNoLabel && !showLabel && 'flex h-full items-center',
        className
      )}
    >
      {showLabel ? (
        <div className="min-w-0 max-w-full space-y-0.5">
          <div
            className={cn(
              'truncate text-sm font-medium text-foreground',
              addressNameClassName
            )}
          >
            {trimmedName}
          </div>
          <div className="flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden">
            {renderedNetwork ? (
              <>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {renderedNetwork}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  ·
                </span>
              </>
            ) : null}
            <span className="truncate font-mono text-xs text-muted-foreground">
              {renderedAddress}
            </span>
            {actions}
          </div>
        </div>
      ) : null}

      {/* Case: no label exists, so show the raw address as primary text. */}
      {!showLabel ? (
        <div className="flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden">
          {renderedNetwork ? (
            <>
              <span className="shrink-0 text-xs text-muted-foreground">
                {renderedNetwork}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">·</span>
            </>
          ) : null}
          <span className="truncate font-mono text-xs text-muted-foreground">
            {renderedAddress}
          </span>
          {actions}
        </div>
      ) : null}
    </div>
  );
}
